import { useState, useRef } from "react"
import { router } from "@inertiajs/react"
import {
  Mic,
  Wand2,
  AudioLines,
  Globe,
  Trash2,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  Loader2,
  CircleCheck,
  Circle,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { VoiceSelector } from "./VoiceSelector"
import { PodcastScriptEditor } from "./PodcastScriptEditor"
import { PodcastStatusBadge } from "./PodcastStatusBadge"
import { usePodcastPolling } from "@/hooks/use-podcast-polling"

const STYLE_LABELS = {
  interview: "Interview",
  deep_dive: "Deep Dive",
  solo: "Solo Narration",
}

const ACTIVE_STATUSES = ["generating_script", "rendering", "uploading"]

export function PodcastPanel({
  article,
  data,
  setData,
  isEditing,
  voices,
  isAdmin = false,
  baseUrl,
  coachPreferences = {},
}) {
  const [generating, setGenerating] = useState(false)
  const [renderingAction, setRenderingAction] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [scriptExpanded, setScriptExpanded] = useState(false)
  const editedScriptRef = useRef(null)

  // Poll for status updates when pipeline is active
  usePodcastPolling(article.podcastStatus)

  const status = article.podcastStatus
  const isActive = ACTIVE_STATUSES.includes(status)
  const needsTwoVoices = data.podcast_style === "interview" || data.podcast_style === "deep_dive"
  const articlePublished = article.status === "published"

  // Podcast config is editable when: in article edit mode OR no podcast has been generated yet
  const canEditConfig = isEditing || !status || status === "draft"
  const configDisabled = !canEditConfig || isActive

  // The two ids arrive as a number from the form state and as a string from the
  // select, so both sides are String()-ed: !== on mixed types would let a
  // duplicate voice straight through to a render that cannot succeed.
  const secondVoiceIsDistinct =
    String(data.podcast_interviewee_voice_id) !== String(data.podcast_host_voice_id)
  const duplicateVoiceSelected =
    needsTwoVoices &&
    !!data.podcast_interviewee_voice_id &&
    !!data.podcast_host_voice_id &&
    !secondVoiceIsDistinct

  const canGenerate =
    !isActive &&
    data.podcast_style &&
    data.podcast_host_voice_id &&
    data.podcast_host_name &&
    (!needsTwoVoices ||
      (data.podcast_interviewee_voice_id &&
        data.podcast_interviewee_name &&
        secondVoiceIsDistinct))

  const handleGenerateScript = () => {
    setGenerating(true)
    router.post(
      `${baseUrl}/articles/${article.id}/generate_podcast_script`,
      {
        podcast_style: data.podcast_style,
        podcast_host_voice_id: data.podcast_host_voice_id,
        podcast_interviewee_voice_id: needsTwoVoices
          ? data.podcast_interviewee_voice_id
          : "",
        podcast_host_name: data.podcast_host_name,
        podcast_interviewee_name: needsTwoVoices
          ? data.podcast_interviewee_name
          : "",
        podcast_additional_notes: data.podcast_additional_notes,
        podcast_host_name_phonetic: data.podcast_host_name_phonetic,
        podcast_interviewee_name_phonetic: needsTwoVoices
          ? data.podcast_interviewee_name_phonetic
          : "",
        podcast_enabled: true,
      },
      { onFinish: () => setGenerating(false) }
    )
  }

  const handleRenderAudio = () => {
    setRenderingAction(true)
    const params = {
      podcast_host_name_phonetic: data.podcast_host_name_phonetic,
      podcast_interviewee_name_phonetic: data.podcast_interviewee_name_phonetic,
    }
    if (editedScriptRef.current) {
      params.podcast_script = JSON.stringify(editedScriptRef.current)
    }
    router.post(
      `${baseUrl}/articles/${article.id}/render_podcast_audio`,
      params,
      { onFinish: () => setRenderingAction(false) }
    )
  }

  const handlePublishPodcast = () => {
    setPublishing(true)
    router.post(`${baseUrl}/articles/${article.id}/publish_podcast`, {}, {
      onFinish: () => setPublishing(false),
    })
  }

  const handleUnpublishPodcast = () => {
    setPublishing(true)
    router.post(`${baseUrl}/articles/${article.id}/unpublish_podcast`, {}, {
      onFinish: () => setPublishing(false),
    })
  }

  const handleRemovePodcast = () => {
    router.post(`${baseUrl}/articles/${article.id}/remove_podcast`)
  }

  const speakers = [
    data.podcast_host_name || article.podcastHostName,
    needsTwoVoices
      ? data.podcast_interviewee_name || article.podcastIntervieweeName
      : null,
  ].filter(Boolean)

  return (
    <Card>
      <CardContent className="p-6 space-y-6">
        {/* Enable Toggle */}
        <div data-tour="podcast-toggle" className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-zinc-900">Enable Podcast</h3>
            <p className="text-sm text-zinc-500">
              Generate an AI podcast from this article
            </p>
          </div>
          <Switch
            checked={data.podcast_enabled}
            onCheckedChange={(checked) => setData("podcast_enabled", checked)}
            disabled={isActive}
          />
        </div>

        {data.podcast_enabled && (
          <>
            {/* Setup Steps Guide — shown when podcast setup is not yet complete */}
            {(!status || status === "draft") && !isAdmin && (() => {
              const steps = [
                { done: !!data.podcast_style, label: "Choose a podcast style" },
                { done: !!data.podcast_host_voice_id && !!data.podcast_host_name, label: "Select a host voice and name" },
                ...(needsTwoVoices ? [{ done: !!data.podcast_interviewee_voice_id && !!data.podcast_interviewee_name && secondVoiceIsDistinct, label: "Select a second voice (different from the host) and name" }] : []),
                { done: false, label: "Generate and review your script" },
              ]

              return (
                <>
                  <Separator />
                  <div className="bg-purple-50 rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-purple-900">
                      Complete these steps before submitting your article:
                    </p>
                    <ol className="space-y-2">
                      {steps.map((step, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          {step.done ? (
                            <CircleCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <Circle className="h-4 w-4 text-purple-300 flex-shrink-0" />
                          )}
                          <span className={step.done ? "text-zinc-500 line-through" : "text-purple-900"}>
                            {step.label}
                          </span>
                        </li>
                      ))}
                    </ol>
                    <p className="text-xs text-purple-600">
                      The podcast title and description are generated automatically with the script.
                    </p>
                  </div>
                </>
              )
            })()}

            <Separator />

            {/* Configuration */}
            <div className="space-y-5">
              {/* Style */}
              <div data-tour="podcast-style-selector" className="space-y-2">
                <Label>Podcast Style</Label>
                <Select
                  value={data.podcast_style}
                  onValueChange={(value) => setData("podcast_style", value)}
                  disabled={configDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interview">
                      Interview — Host interviews the guest
                    </SelectItem>
                    <SelectItem value="deep_dive">
                      Deep Dive — Two hosts explore the topic
                    </SelectItem>
                    <SelectItem value="solo">
                      Solo Narration — Single narrator summary
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Host */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Host Name</Label>
                  <Input
                    value={data.podcast_host_name}
                    onChange={(e) =>
                      setData("podcast_host_name", e.target.value)
                    }
                    disabled={configDisabled}
                    placeholder="Name of the podcast host"
                  />
                </div>
                {data.podcast_host_name && (
                  <div className="space-y-2">
                    <Label>Pronunciation</Label>
                    <Input
                      value={data.podcast_host_name_phonetic}
                      onChange={(e) =>
                        setData("podcast_host_name_phonetic", e.target.value)
                      }
                      placeholder="e.g., duh-WAYNE JOHN-son"
                    />
                    <p className="text-xs text-zinc-500">
                      Optional — spell the way it sounds so the AI pronounces it
                      correctly. Break it into syllables with dashes and capitalize
                      the stressed syllable (e.g., Dwayne = duh-WAYNE).
                      Not sure? Ask Claude or ChatGPT to spell it phonetically for you.
                    </p>
                  </div>
                )}
                <VoiceSelector
                  label="Host Voice"
                  voices={voices}
                  value={data.podcast_host_voice_id}
                  onChange={(id) => setData("podcast_host_voice_id", id)}
                  disabled={configDisabled}
                />
              </div>

              {/* Interviewee (only for interview/deep_dive) */}
              {needsTwoVoices && (
                <div className="space-y-3">
                  <Separator />
                  <div className="space-y-2">
                    <Label>
                      {data.podcast_style === "interview"
                        ? "Interviewee Name"
                        : "Second Host Name"}
                    </Label>
                    <Input
                      value={data.podcast_interviewee_name}
                      onChange={(e) =>
                        setData("podcast_interviewee_name", e.target.value)
                      }
                      disabled={configDisabled}
                      placeholder={
                        data.podcast_style === "interview"
                          ? "Name of the interviewee"
                          : "Name of the second host"
                      }
                    />
                  </div>
                  {data.podcast_interviewee_name && (
                    <div className="space-y-2">
                      <Label>Pronunciation</Label>
                      <Input
                        value={data.podcast_interviewee_name_phonetic}
                        onChange={(e) =>
                          setData("podcast_interviewee_name_phonetic", e.target.value)
                        }
                        placeholder="e.g., KRISS-teen"
                      />
                      <p className="text-xs text-zinc-500">
                        Optional — spell the way it sounds so the AI pronounces it
                        correctly. Break it into syllables with dashes and capitalize
                        the stressed syllable (e.g., Christine = KRISS-teen).
                        Not sure? Ask Claude or ChatGPT to spell it phonetically for you.
                      </p>
                    </div>
                  )}
                  <VoiceSelector
                    label={
                      data.podcast_style === "interview"
                        ? "Interviewee Voice"
                        : "Second Host Voice"
                    }
                    voices={voices}
                    value={data.podcast_interviewee_voice_id}
                    onChange={(id) =>
                      setData("podcast_interviewee_voice_id", id)
                    }
                    disabled={configDisabled}
                  />
                  {duplicateVoiceSelected && (
                    <p className="text-xs text-amber-600">
                      The second voice must be different from the host voice.
                    </p>
                  )}
                </div>
              )}

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label>Additional Notes for AI</Label>
                <Textarea
                  value={data.podcast_additional_notes}
                  onChange={(e) =>
                    setData("podcast_additional_notes", e.target.value)
                  }
                  disabled={configDisabled}
                  rows={2}
                  placeholder="Any additional context or instructions for the AI script generator"
                />
              </div>
            </div>

            <Separator />

            {/* Pipeline Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-zinc-900">
                  Podcast Pipeline
                </h3>
                {status && <PodcastStatusBadge status={status} />}
              </div>

              {/* No status or draft — Generate button */}
              {(!status || status === "draft") && (
                <div className="bg-zinc-50 rounded-lg p-4 text-center space-y-3">
                  <Mic className="h-8 w-8 text-zinc-400 mx-auto" />
                  <p className="text-sm text-zinc-600">
                    Configure your podcast settings above, then generate a script.
                  </p>
                  <Button
                    data-tour="podcast-generate-btn"
                    variant="gradient"
                    onClick={handleGenerateScript}
                    disabled={!canGenerate || generating}
                  >
                    <Wand2 className="h-4 w-4" />
                    {generating ? "Starting..." : "Generate Script"}
                  </Button>
                  {!canGenerate && data.podcast_style && (
                    <p className="text-xs text-zinc-400">
                      Fill in all required fields to generate.
                    </p>
                  )}
                </div>
              )}

              {/* Generating script — wait state */}
              {status === "generating_script" && (
                <div className="bg-amber-50 rounded-lg p-4 text-center space-y-2">
                  <p className="text-sm font-medium text-amber-800">
                    Generating your podcast script...
                  </p>
                  <p className="text-xs text-amber-600">
                    This usually takes 30-60 seconds. The page will update automatically.
                  </p>
                </div>
              )}

              {/* Script generated — review & render */}
              {status === "script_generated" && (
                <div className="space-y-4">
                  {/* Title & Show Notes (read-only) */}
                  {article.podcastTitle && (
                    <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-medium text-blue-900">
                        {article.podcastTitle}
                      </p>
                      {article.podcastShowNotes && (
                        <p className="text-xs text-blue-700">
                          {article.podcastShowNotes}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Script Editor (collapsible) */}
                  <div data-tour="podcast-script-editor" className="rounded-lg border border-zinc-200">
                    <button
                      type="button"
                      onClick={() => setScriptExpanded((prev) => !prev)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50 transition-colors rounded-lg"
                    >
                      <span className="text-sm font-medium text-zinc-900">
                        Script ({article.podcastScript?.length || 0} segments)
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-zinc-500 transition-transform ${
                          scriptExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {scriptExpanded && (
                      <div className="px-4 pb-4">
                        <PodcastScriptEditor
                          initialSegments={article.podcastScript}
                          onChange={(segs) => {
                            editedScriptRef.current = segs
                          }}
                          disabled={false}
                          speakers={speakers}
                        />
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    {!isAdmin && !articlePublished ? (
                      <div className="w-full bg-amber-50 rounded-lg p-4 space-y-1">
                        <p className="text-sm font-medium text-amber-800">
                          Script is ready!
                        </p>
                        <p className="text-xs text-amber-600">
                          Submit the article for review — once an admin publishes it, you can render the audio.
                        </p>
                      </div>
                    ) : (
                      <Button
                        data-tour="podcast-render-audio-btn"
                        variant="gradient"
                        onClick={handleRenderAudio}
                        disabled={renderingAction}
                      >
                        <AudioLines className="h-4 w-4" />
                        {renderingAction ? "Starting..." : "Render Audio"}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={handleGenerateScript}
                      disabled={generating}
                    >
                      <RefreshCw className="h-4 w-4" />
                      Regenerate Script
                    </Button>
                  </div>
                </div>
              )}

              {/* Rendering — wait state */}
              {status === "rendering" && (
                <div className="bg-amber-50 rounded-lg p-4 text-center space-y-2">
                  <p className="text-sm font-medium text-amber-800">
                    Rendering your podcast audio...
                  </p>
                  <p className="text-xs text-amber-600">
                    This can take 5-10 minutes. The page will update automatically.
                  </p>
                </div>
              )}

              {/* Uploading — wait state */}
              {status === "uploading" && (
                <div className="bg-amber-50 rounded-lg p-4 text-center space-y-2">
                  <p className="text-sm font-medium text-amber-800">
                    Uploading to Podbean...
                  </p>
                  <p className="text-xs text-amber-600">
                    Almost done! The page will update automatically.
                  </p>
                </div>
              )}

              {/* Uploaded — ready to publish */}
              {status === "uploaded" && (
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-emerald-800">
                      Podcast audio is ready!
                    </p>
                    {article.podcastUrl && (
                      <a
                        href={article.podcastUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:underline"
                      >
                        Preview on Podbean
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Button
                        onClick={handlePublishPodcast}
                        disabled={publishing}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {publishing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Globe className="h-4 w-4" />
                        )}
                        {publishing ? "Publishing..." : "Publish Podcast"}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Published */}
              {status === "published" && (
                <div className="space-y-4">
                  <div className="bg-green-50 rounded-lg p-4 space-y-3">
                    <p className="text-sm font-medium text-green-800">
                      Podcast is published!
                    </p>
                    {article.podcastUrl && (
                      <PodcastEmbed url={article.podcastUrl} />
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      onClick={handleUnpublishPodcast}
                      disabled={publishing}
                    >
                      {publishing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : null}
                      {publishing ? "Unpublishing..." : "Unpublish Podcast"}
                    </Button>
                  )}
                </div>
              )}

              {/* Failed */}
              {status === "failed" && (
                <div className="bg-red-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        Podcast generation failed
                      </p>
                      {article.podcastErrorMessage && (
                        <p className="text-xs text-red-600 mt-1">
                          {article.podcastErrorMessage}
                        </p>
                      )}
                      {article.podcastFailedAtStep && (
                        <p className="text-xs text-red-500 mt-1">
                          Failed at: {article.podcastFailedAtStep}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateScript}
                    disabled={generating}
                    className="border-red-200 text-red-700 hover:bg-red-100"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                  </Button>
                </div>
              )}

              {/* Admin: Remove podcast (when uploaded or published) */}
              {isAdmin && status && !isActive && status !== "failed" && (
                <div className="pt-2 border-t border-zinc-200">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove Podcast
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Podcast</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove all podcast data including the script,
                          audio, and Podbean episode. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleRemovePodcast}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function PodcastEmbed({ url }) {
  if (!url) return null

  // Extract the "i" parameter if it's a full Podbean URL, or use as-is
  let embedId = url
  try {
    const urlObj = new URL(url)
    const iParam = urlObj.searchParams.get("i")
    if (iParam) embedId = iParam
  } catch {
    // url might just be the episode ID itself
  }

  const embedUrl = `https://www.podbean.com/player-v2/?i=${embedId}&share=1&download=1&fonts=Arial&skin=1&btn-skin=7&size=150`

  return (
    <iframe
      src={embedUrl}
      style={{ border: "none", minWidth: "min(100%,430px)", height: "150px" }}
      scrolling="no"
      allow="autoplay"
      title="Podcast player"
    />
  )
}
